import { DateTime } from 'luxon';
import { ConflictRec } from '~/common/types';

export const recommendationInfo: { [rec in ConflictRec]: { label: string; description: string } } = {
  [ConflictRec.Accelerate]: {
    label: 'Accelerate',
    description: 'Project may avoid significant conflict if the schedule is moved closer to the present.',
  },
  [ConflictRec.Defer]: {
    label: 'Defer',
    description: 'Project may avoid significant conflict if the schedule is moved further in the future.',
  },
  [ConflictRec.NeedsReview]: {
    label: 'Needs Review',
    description: 'Project may have unavoidable conflicts to manage.',
  },
  [ConflictRec.NoScheduleConflict]: {
    label: 'No Schedule Conflict',
    description: 'Project has no conflicts that match the current construction schedule window.',
  },
};

/** Chip color for a schedule recommendation: Defer red, No Schedule Conflict green, others yellow. */
export function recommendationChipVariant(r: ConflictRec): 'yellow' | 'green' | 'red' {
  if (r === ConflictRec.Defer) return 'red';
  if (r === ConflictRec.NoScheduleConflict) return 'green';
  return 'yellow';
}

/**
 * Tooltip content for this project's current schedule recommendation, with the Accelerate/Defer
 * titles showing the actual target date computed from this project's conflict window.
 * The intelligence API returns `conflictsStart`/`conflictsEnd` as raw ISO strings despite
 * their `DateTime` type, so they're parsed here before any DateTime math.
 */
export function buildRecommendationDetail(
  rec: ConflictRec,
  conflictsStart: DateTime | string | undefined,
  conflictsEnd: DateTime | string | undefined,
  projectStart: DateTime | undefined,
  projectEnd: DateTime | undefined,
): { title: string; description: string } {
  const { label, description } = recommendationInfo[rec];

  if (rec === ConflictRec.Accelerate) {
    const start = conflictsStart ? DateTime.fromISO(String(conflictsStart)) : undefined;
    if (start && projectStart && projectEnd) {
      const rollback = start.minus(projectEnd.diff(projectStart));
      if (rollback) {
        return { title: `Accelerated to: ${rollback.toLocaleString()}`, description };
      }
    }
  }

  if (rec === ConflictRec.Defer) {
    const end = conflictsEnd ? DateTime.fromISO(String(conflictsEnd)) : undefined;
    if (end) {
      return { title: `Deferred to: ${end.toLocaleString()}`, description };
    }
  }

  return { title: label, description };
}
